export const getInfiniteQueryListNextParam = (
  lastPage: List,
  allPages: List[],
  lastPageParam: number,
) => {
  // A page that came back empty is the end of the list, whatever `count` claims.
  // Without this, a count that disagrees with the items it counted — one that
  // ignores the filter the items were filtered by, say — keeps the trigger asking
  // for the next page forever.
  if (lastPage.items.length === 0) {
    return null;
  }
  if (allPages.flatMap(({ items }) => items).length < lastPage.count) {
    return lastPageParam + 1;
  }
  return null;
};

/**
 * One list out of an infinite query's pages, first copy of each id winning.
 *
 * A LIMIT/OFFSET endpoint can hand the same record back twice — within a page if
 * its query joins without collapsing the rows, across pages if its ordering isn't
 * unique — and React needs the keys the records render under to be unique. The
 * duplicate is the server's to fix; rendering it twice is ours.
 */
export const flattenInfinitePages = <T extends { id: string }>(pages?: { items: T[] }[]): T[] => {
  const byId = new Map<string, T>();
  for (const { items } of pages ?? []) {
    for (const item of items) {
      if (!byId.has(item.id)) {
        byId.set(item.id, item);
      }
    }
  }
  return [...byId.values()];
};

export interface List<T = unknown> {
  count: number;
  items: T[];
}

/**
 * The wire shape of one step of a cursor stream (`limit` + `cursor`, no `page`).
 * `has_more` and `next_cursor` are absent rather than null on the last step;
 * `count` only comes with `with_count=true`.
 */
export interface CursorPageDto<T> {
  count?: number;
  has_more?: boolean;
  items: T[];
  next_cursor?: string;
}

/**
 * One step of a cursor stream. "Is there more" is `hasMore`, never arithmetic
 * against a page size: a step can come back full and still be the last one.
 */
export interface CursorPage<T> {
  hasMore: boolean;
  items: T[];
  nextCursor: null | string;
}

export const toCursorPage = <T>(dto: CursorPageDto<T>): CursorPage<T> => ({
  items: dto.items ?? [],
  hasMore: dto.has_more ?? false,
  nextCursor: dto.next_cursor ?? null,
});
